import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { CpmButton, CpmCheckbox, CpmChip, CpmEmptyState } from '../../../components/design-system';
import type { CpmState } from '../../../types/centralProductMaster.types';
import type {
    ConfigTemplateLevel,
    ConfigTemplateTargetBrand,
    ConfigTemplateTargetStore,
} from '../../../types/configTemplate.types';
import {
    CONFIG_TEMPLATE_COPY,
    applyPageNote,
    applySelectAllLabel,
    applySelectDistrictLabel,
    applySelectedLine,
    copyBrandMeta,
    copyCountLine,
    copyDistrictCount,
    copyEmptyBody,
    copyFailBody,
    copyFailRetry,
    copyFailTitle,
    copyFilteredEmptyBody,
    copyFilteredEmptyTitle,
    copyLoadingLine,
    copyNeedsQueryBody,
    copyPageNote,
    copyStoreMeta,
    copyWaitingLine,
} from '../configTemplateCopy';
import type { TargetItem, useTargetPicker } from '../hooks/useTargetPicker';
import { toStateFilterOptions } from '../utils/stateCatalog';
import InfiniteScrollSentinel from './InfiniteScrollSentinel';
import PickerFilterPopover, { PickerPulseLine, PickerSearchInput } from './PickerFilterPopover';

interface TargetPickerListProps {
    level: ConfigTemplateLevel;
    picker: ReturnType<typeof useTargetPicker>;
    states: CpmState[];
    single: boolean;
    fill?: boolean;
    compact?: boolean;
    showStateFilter?: boolean;
    disabled?: boolean;
    statesStatus?: 'idle' | 'loading' | 'success' | 'error';
    onRetryStates?: () => void;
    leading?: ReactNode;
}

function isStore(item: TargetItem, level: ConfigTemplateLevel): item is ConfigTemplateTargetStore {
    return level === 'STORE' && 'district' in item;
}

function CheckIcon() {
    return (
        <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--cpm-brand-purple)"
            strokeWidth="3"
        >
            <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function SearchGlyph({ size = 28 }: { size?: number }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
        >
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.35-4.35" strokeLinecap="round" />
        </svg>
    );
}

function PickerRowShimmer({ rows, compact }: { rows: number; compact?: boolean }) {
    return (
        <div className="flex flex-col" style={{ gap: compact ? 8 : 14 }}>
            {Array.from({ length: rows }).map((_, index) => (
                <span
                    key={index}
                    className="cpm-sk"
                    style={{ display: 'block', width: '100%', height: 46, borderRadius: 10 }}
                />
            ))}
        </div>
    );
}

function IllustratedEmpty({
    title,
    body,
    action,
    iconSize = 28,
    compact = false,
}: {
    title: string;
    body: string;
    action?: ReactNode;
    iconSize?: number;
    compact?: boolean;
}) {
    return (
        <div
            className="flex flex-col items-center text-center"
            style={{ gap: compact ? 8 : 12, padding: compact ? '14px 0 8px' : '26px 0 18px' }}
        >
            <div
                className="flex items-center justify-center"
                style={{
                    width: 64,
                    height: 64,
                    borderRadius: 18,
                    background: 'var(--cpm-purple-tint-13)',
                    color: 'var(--cpm-brand-purple)',
                }}
            >
                <SearchGlyph size={iconSize} />
            </div>
            <div style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--cpm-text-primary)' }}>
                {title}
            </div>
            <div
                style={{
                    fontSize: 12.5,
                    fontWeight: 500,
                    lineHeight: 1.55,
                    color: 'var(--cpm-text-secondary)',
                    maxWidth: 440,
                    textWrap: 'pretty',
                }}
            >
                {body}
            </div>
            {action}
        </div>
    );
}

function TargetRow({
    name,
    meta,
    selected,
    single,
    compact,
    onSelect,
}: {
    name: string;
    meta: string;
    selected: boolean;
    single: boolean;
    compact?: boolean;
    onSelect: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onSelect}
            className="flex w-full items-center text-left border border-[var(--cpm-border-default)] hover:border-[var(--cpm-brand-purple)]"
            style={{
                gap: compact ? 10 : 12,
                padding: compact ? '7px 10px' : '10px 12px',
                borderRadius: 10,
                marginBottom: compact ? 4 : 7,
                cursor: 'pointer',
                background: selected ? 'var(--cpm-purple-tint-7)' : '#fff',
            }}
        >
            {!single ? (
                <span className="pointer-events-none">
                    <CpmCheckbox checked={selected} onChange={() => undefined} />
                </span>
            ) : null}
            <div className="min-w-0 flex-1">
                <div
                    className="truncate"
                    style={{
                        fontSize: 13.5,
                        fontWeight: 800,
                        letterSpacing: '-0.01em',
                        color: 'var(--cpm-text-primary)',
                    }}
                >
                    {name}
                </div>
                {meta ? (
                    <div
                        style={{
                            fontSize: 11.5,
                            fontWeight: 600,
                            color: 'var(--cpm-text-secondary)',
                            marginTop: compact ? 1 : 2,
                        }}
                    >
                        {meta}
                    </div>
                ) : null}
            </div>
            {single && selected ? <CheckIcon /> : null}
        </button>
    );
}

export default function TargetPickerList({
    level,
    picker,
    states,
    single,
    fill = single,
    compact = false,
    showStateFilter = true,
    disabled = false,
    statesStatus = 'success',
    onRetryStates,
    leading,
}: TargetPickerListProps) {
    const [openDistricts, setOpenDistricts] = useState<Record<string, boolean>>({});
    const stateOptions = toStateFilterOptions(states);
    const shouldGroup = level === 'STORE' && picker.brandFilters.length !== 1;
    const catalogTotal = picker.catalogTotal ?? 0;

    const grouped = useMemo(() => {
        if (!shouldGroup) return null;
        return picker.items.reduce<Record<string, typeof picker.items>>((acc, item) => {
            const district = isStore(item, level) ? item.district || 'Other' : 'Other';
            if (!acc[district]) acc[district] = [];
            acc[district].push(item);
            return acc;
        }, {});
    }, [level, picker.items, shouldGroup]);

    const applied: { key: string; label: string; clear: () => void }[] = [];
    if (picker.searchInput.trim()) {
        applied.push({
            key: 'search',
            label: `Search: ${picker.searchInput.trim()}`,
            clear: () => picker.onSearchChange(''),
        });
    }
    picker.stateFilters.forEach((state) => {
        applied.push({
            key: `state-${state}`,
            label: state,
            clear: () =>
                picker.setStateFilters(picker.stateFilters.filter((item) => item !== state)),
        });
    });
    picker.brandFilters.forEach((id) => {
        const brand =
            picker.selected[id] ??
            picker.items.find((item) => item._id === id) ??
            picker.brandOptions.find((item) => item._id === id);
        applied.push({
            key: `brand-${id}`,
            label: `Brand: ${brand?.name ?? id}`,
            clear: () => picker.setBrandFilters(picker.brandFilters.filter((item) => item !== id)),
        });
    });

    const listFailed =
        picker.catalogStatus === 'error' || (picker.status === 'error' && !picker.gated);
    const initialLoading =
        (picker.catalogStatus === 'loading' || picker.catalogStatus === 'idle') &&
        picker.gated &&
        !picker.trueEmpty;
    const counting =
        !picker.gated &&
        (picker.waitingSearch || (picker.status === 'loading' && picker.items.length === 0));
    const showRows =
        !picker.gated &&
        !listFailed &&
        !picker.trueEmpty &&
        !picker.filteredEmpty &&
        !counting &&
        picker.items.length > 0;

    const countLine = copyCountLine({
        level,
        gated: picker.gated,
        ready: picker.status === 'success' || picker.catalogStatus === 'success',
        counting: counting || picker.status === 'loading',
        matching: picker.total,
        catalogTotal: picker.catalogTotal,
    });

    const rowMeta = (item: (typeof picker.items)[number]) => {
        if (level === 'STORE_BRAND') {
            return copyBrandMeta(item as ConfigTemplateTargetBrand);
        }
        return copyStoreMeta(item as ConfigTemplateTargetStore);
    };

    return (
        <div className={fill ? 'flex min-h-0 flex-1 flex-col' : 'flex flex-col'}>
            <div
                className="flex flex-wrap items-center"
                style={{
                    gap: compact ? 8 : 10,
                    padding: compact ? '8px 20px' : '13px 20px',
                    borderBottom: '1px solid var(--cpm-border-default)',
                }}
            >
                <PickerSearchInput
                    value={picker.searchInput}
                    onChange={picker.onSearchChange}
                    disabled={disabled}
                    placeholder={
                        level === 'STORE'
                            ? CONFIG_TEMPLATE_COPY.COPY_SEARCH_STORE
                            : CONFIG_TEMPLATE_COPY.COPY_SEARCH_BRAND
                    }
                />
                {showStateFilter ? (
                    <PickerFilterPopover
                        label={
                            single
                                ? CONFIG_TEMPLATE_COPY.COPY_STATE_CHIP
                                : CONFIG_TEMPLATE_COPY.STATES
                        }
                        header={CONFIG_TEMPLATE_COPY.STATES}
                        options={stateOptions}
                        selected={picker.stateFilters}
                        onChange={picker.setStateFilters}
                        disabled={disabled}
                        loading={statesStatus === 'loading' || statesStatus === 'idle'}
                        loadingLine={CONFIG_TEMPLATE_COPY.COPY_STATE_OPTIONS_WAIT}
                        failed={statesStatus === 'error'}
                        failTitle={CONFIG_TEMPLATE_COPY.LOAD_FAIL_STATES}
                        failBody={CONFIG_TEMPLATE_COPY.COPY_OPTIONS_UNCHANGED}
                        onRetry={onRetryStates}
                    />
                ) : null}
                {level === 'STORE' ? (
                    <PickerFilterPopover
                        label={CONFIG_TEMPLATE_COPY.COPY_STORE_BRAND_CHIP}
                        header={CONFIG_TEMPLATE_COPY.COPY_STORE_BRAND_CHIP}
                        options={picker.brandOptions.map((brand) => ({
                            value: brand._id,
                            label:
                                typeof brand.storeCount === 'number'
                                    ? `${brand.name} · ${brand.storeCount} ${brand.storeCount === 1 ? 'store' : 'stores'}`
                                    : brand.name,
                        }))}
                        selected={picker.brandFilters}
                        onChange={picker.setBrandFilters}
                        disabled={disabled}
                        loading={
                            picker.brandOptionsStatus === 'loading' ||
                            picker.brandOptionsStatus === 'idle'
                        }
                        loadingLine={CONFIG_TEMPLATE_COPY.COPY_BRAND_OPTIONS_WAIT}
                        failed={picker.brandOptionsStatus === 'error'}
                        failTitle={CONFIG_TEMPLATE_COPY.COPY_BRAND_OPTIONS_FAIL}
                        failBody={CONFIG_TEMPLATE_COPY.COPY_OPTIONS_UNCHANGED}
                        onRetry={picker.retryBrandOptions}
                        onOpen={picker.ensureBrandOptions}
                        search={{
                            value: picker.brandOptionInput,
                            onChange: picker.onBrandOptionQueryChange,
                            placeholder: CONFIG_TEMPLATE_COPY.COPY_BRAND_SEARCH,
                            noHit:
                                picker.brandOptionsStatus === 'success' &&
                                picker.brandOptions.length === 0 &&
                                picker.brandOptionInput.trim().length > 0,
                            noHitLabel: CONFIG_TEMPLATE_COPY.COPY_BRAND_NO_HIT,
                        }}
                    />
                ) : null}
                <div className="flex-1" />
                {countLine ? (
                    <span
                        style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: 'var(--cpm-text-secondary)',
                        }}
                    >
                        {countLine}
                    </span>
                ) : null}
            </div>

            {applied.length > 0 ? (
                <div
                    className="flex flex-wrap items-center"
                    style={{
                        gap: 8,
                        padding: compact ? '6px 20px' : '10px 20px',
                        borderBottom: '1px solid var(--cpm-border-default)',
                        background: 'var(--cpm-purple-tint-3)',
                    }}
                >
                    <span
                        className="uppercase"
                        style={{
                            fontSize: 10.5,
                            fontWeight: 800,
                            letterSpacing: '0.08em',
                            color: 'var(--cpm-brand-purple)',
                        }}
                    >
                        {CONFIG_TEMPLATE_COPY.FILTERS}
                    </span>
                    {applied.map((filter) => (
                        <CpmChip key={filter.key} variant="token" onRemove={filter.clear}>
                            {filter.label}
                        </CpmChip>
                    ))}
                    <CpmButton variant="ghost" size="sm" onClick={picker.clearFilters}>
                        {CONFIG_TEMPLATE_COPY.CLEAR_ALL}
                    </CpmButton>
                </div>
            ) : null}

            {leading}

            {!single ? (
                <div
                    className="flex flex-wrap items-center"
                    style={{
                        gap: 8,
                        padding: compact ? '8px 20px' : '11px 20px',
                        borderBottom: '1px solid var(--cpm-border-default)',
                    }}
                >
                    <span
                        style={{
                            fontSize: 12.5,
                            fontWeight: 800,
                            color: 'var(--cpm-text-primary)',
                        }}
                    >
                        {applySelectedLine(picker.selectedCount)}
                    </span>
                    <div className="flex-1" />
                    <CpmButton
                        variant="outline"
                        size="sm"
                        disabled={disabled || picker.total === 0}
                        onClick={picker.selectAllMatching}
                        style={{ color: 'var(--cpm-text-primary)' }}
                    >
                        {applySelectAllLabel(picker.total)}
                    </CpmButton>
                    {picker.selectedCount > 0 ? (
                        <CpmButton
                            variant="ghost"
                            size="sm"
                            disabled={disabled}
                            onClick={picker.clearSelection}
                        >
                            {CONFIG_TEMPLATE_COPY.CLEAR_SELECTION}
                        </CpmButton>
                    ) : null}
                </div>
            ) : null}

            <div
                className={fill ? 'min-h-0 flex-1 overflow-y-auto' : 'overflow-y-auto'}
                style={{
                    padding: fill ? (compact ? '8px 20px 10px' : '14px 20px 18px') : '4px 0 0',
                    maxHeight: fill ? undefined : 360,
                    minHeight: fill ? (compact ? 200 : 280) : undefined,
                }}
            >
                {picker.trueEmpty ? (
                    <CpmEmptyState
                        title={
                            level === 'STORE_BRAND'
                                ? CONFIG_TEMPLATE_COPY.NO_STORE_BRANDS
                                : CONFIG_TEMPLATE_COPY.NO_STORES
                        }
                        description={copyEmptyBody(level)}
                    />
                ) : null}

                {initialLoading ? (
                    <div className="flex flex-col" style={{ gap: compact ? 8 : 14 }}>
                        <PickerPulseLine>{copyLoadingLine(level)}</PickerPulseLine>
                        <PickerRowShimmer rows={4} compact={compact} />
                    </div>
                ) : null}

                {listFailed ? (
                    <div className="flex flex-col items-start" style={{ gap: 12 }}>
                        <div
                            style={{
                                fontSize: 14.5,
                                fontWeight: 800,
                                color: 'var(--cpm-text-primary)',
                            }}
                        >
                            {copyFailTitle(level)}
                        </div>
                        <div
                            style={{
                                fontSize: 12.5,
                                fontWeight: 500,
                                lineHeight: 1.55,
                                color: 'var(--cpm-text-secondary)',
                                maxWidth: 520,
                                textWrap: 'pretty',
                            }}
                        >
                            {copyFailBody()}
                        </div>
                        <CpmButton variant="navy" size="sm" onClick={picker.retry}>
                            {copyFailRetry(level)}
                        </CpmButton>
                    </div>
                ) : null}

                {!initialLoading && !listFailed && !picker.trueEmpty && picker.gated ? (
                    <IllustratedEmpty
                        title={CONFIG_TEMPLATE_COPY.COPY_EMPTY_UNTIL}
                        body={copyNeedsQueryBody(level, catalogTotal)}
                        compact={compact}
                    />
                ) : null}

                {counting ? (
                    <div className="flex flex-col" style={{ gap: compact ? 8 : 14 }}>
                        <PickerPulseLine>{copyWaitingLine(level)}</PickerPulseLine>
                        <PickerRowShimmer rows={3} compact={compact} />
                    </div>
                ) : null}

                {picker.filteredEmpty ? (
                    <IllustratedEmpty
                        title={copyFilteredEmptyTitle(level)}
                        body={copyFilteredEmptyBody(level)}
                        iconSize={32}
                        compact={compact}
                        action={
                            <CpmButton variant="navy" size="md" onClick={picker.clearFilters}>
                                {CONFIG_TEMPLATE_COPY.COPY_CLEAR_SEARCH_FILTERS}
                            </CpmButton>
                        }
                    />
                ) : null}

                {showRows && grouped ? (
                    <div>
                        <div
                            style={{
                                fontSize: 11.5,
                                fontWeight: 600,
                                color: 'var(--cpm-text-secondary)',
                                marginBottom: compact ? 6 : 9,
                            }}
                        >
                            {single
                                ? CONFIG_TEMPLATE_COPY.COPY_GROUPED_BY_DISTRICT
                                : CONFIG_TEMPLATE_COPY.APPLY_GROUPED_BY_DISTRICT}
                        </div>
                        {Object.entries(grouped).map(([district, rows]) => {
                            const open = openDistricts[district] !== false;
                            const visible = open ? rows : [];
                            return (
                                <div
                                    key={district}
                                    style={{
                                        border: '1px solid var(--cpm-border-default)',
                                        borderRadius: 10,
                                        marginBottom: compact ? 5 : 8,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <div
                                        className="flex w-full items-center"
                                        style={{
                                            gap: compact ? 8 : 10,
                                            padding: compact ? '7px 10px' : '10px 12px',
                                            background: 'var(--cpm-surface-subtle)',
                                        }}
                                    >
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setOpenDistricts((prev) => ({
                                                    ...prev,
                                                    [district]: !open,
                                                }))
                                            }
                                            className="flex items-center min-w-0"
                                            style={{
                                                gap: 8,
                                                border: 0,
                                                background: 'transparent',
                                                cursor: 'pointer',
                                                padding: 0,
                                                fontFamily: 'inherit',
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontSize: 13,
                                                    fontWeight: 800,
                                                    color: 'var(--cpm-text-primary)',
                                                }}
                                            >
                                                {open ? '–' : '+'}
                                            </span>
                                            <span
                                                style={{
                                                    fontSize: 13,
                                                    fontWeight: 800,
                                                    color: 'var(--cpm-text-primary)',
                                                }}
                                            >
                                                {district}
                                            </span>
                                            <span
                                                style={{
                                                    fontSize: 11.5,
                                                    fontWeight: 700,
                                                    color: 'var(--cpm-text-secondary)',
                                                }}
                                            >
                                                {copyDistrictCount(rows.length)}
                                            </span>
                                        </button>
                                        <div className="flex-1" />
                                        {!single ? (
                                            <CpmButton
                                                variant="ghost"
                                                size="sm"
                                                disabled={disabled}
                                                onClick={() => picker.selectGroup(rows)}
                                            >
                                                {applySelectDistrictLabel(rows.length)}
                                            </CpmButton>
                                        ) : null}
                                    </div>
                                    {open ? (
                                        <div
                                            style={{
                                                padding: compact ? '6px 8px 7px' : '8px 10px 10px',
                                            }}
                                        >
                                            {visible.map((item) => (
                                                <TargetRow
                                                    key={item._id}
                                                    name={item.name}
                                                    meta={rowMeta(item)}
                                                    selected={picker.isSelected(item._id)}
                                                    single={single}
                                                    compact={compact}
                                                    onSelect={() => picker.toggle(item, single)}
                                                />
                                            ))}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                ) : null}

                {showRows && !grouped ? (
                    <div>
                        {(
                            single
                                ? copyPageNote(picker.items.length, picker.total)
                                : applyPageNote(picker.items.length, picker.total)
                        ) ? (
                            <div
                                style={{
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    color: 'var(--cpm-text-secondary)',
                                    marginBottom: compact ? 6 : 9,
                                    textWrap: 'pretty',
                                }}
                            >
                                {single
                                    ? copyPageNote(picker.items.length, picker.total)
                                    : applyPageNote(picker.items.length, picker.total)}
                            </div>
                        ) : null}
                        {picker.items.map((item) => (
                            <TargetRow
                                key={item._id}
                                name={item.name}
                                meta={rowMeta(item)}
                                selected={picker.isSelected(item._id)}
                                single={single}
                                compact={compact}
                                onSelect={() => picker.toggle(item, single)}
                            />
                        ))}
                    </div>
                ) : null}

                {showRows ? (
                    <InfiniteScrollSentinel
                        hasMore={picker.canLoadMore}
                        loading={picker.loadingMore}
                        error={picker.moreError}
                        disabled={disabled}
                        onLoadMore={picker.loadMore}
                        pageSize={25}
                        showEnd={picker.items.length > 0}
                        compact={compact}
                    />
                ) : null}
            </div>
        </div>
    );
}
